export function radians(d) { return (d * Math.PI) / 180; }
export function degrees(r) { return (r * 180) / Math.PI; }

export function enuToLatLon(east_m, north_m, lat0_deg, lon0_deg) {
  const R = 6378137;
  const lat0 = radians(lat0_deg);
  const dLat = north_m / R;
  const dLon = east_m / (R * Math.cos(lat0));
  const lat = lat0 + dLat;
  const lon = radians(lon0_deg) + dLon;
  return [degrees(lat), degrees(lon)];
}

export function latLonToEnu(lat_deg, lon_deg, lat0_deg, lon0_deg) {
  const R = 6378137;
  const lat0 = radians(lat0_deg);
  const dLat = radians(lat_deg - lat0_deg);
  const dLon = radians(lon_deg - lon0_deg);
  const north = dLat * R;
  const east = dLon * R * Math.cos(lat0);
  return [east, north];
}

export function rotatePoint([x, y], ang) {
  const c = Math.cos(ang), s = Math.sin(ang);
  return [x * c - y * s, x * s + y * c];
}

export function pointInPoly([x, y], poly) {
  let inside = false;
  const eps = 1e-9;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];

    // Treat points lying exactly on an edge as inside. This avoids
    // losing placements when a block touches the polygon boundary.
    const cross = (x - xi) * (yj - yi) - (y - yi) * (xj - xi);
    const onSeg =
      Math.abs(cross) < eps &&
      x >= Math.min(xi, xj) - eps &&
      x <= Math.max(xi, xj) + eps &&
      y >= Math.min(yi, yj) - eps &&
      y <= Math.max(yi, yj) + eps;
    if (onSeg) return true;

    const intersect =
      (yi > y) !== (yj > y) &&
      x <= ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function polygonBounds(poly) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of poly) {
    if (x < minX) minX = x; if (y < minY) minY = y;
    if (x > maxX) maxX = x; if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

export function polygonArea(pts) {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

export function polygonCentroid(pts) {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    const f = x1 * y2 - x2 * y1;
    a += f;
    cx += (x1 + x2) * f;
    cy += (y1 + y2) * f;
  }
  if (a === 0) return pts[0];
  a *= 0.5;
  cx /= 6 * a;
  cy /= 6 * a;
  return [cx, cy];
}

export function maxPlacements(
  poly,
  rowStep,
  colStep,
  halfW,
  halfH,
  alignGrid = false
) {
  const b = polygonBounds(poly);
  if (
    !(isFinite(b.minX) && isFinite(b.minY) && isFinite(b.maxX) && isFinite(b.maxY))
  )
    return 0;
  const xOffsets = alignGrid
    ? [0]
    : Array.from({ length: 10 }, (_, i) => (colStep * i) / 10);
  const yOffsets = alignGrid
    ? [0]
    : Array.from({ length: 10 }, (_, i) => (rowStep * i) / 10);
  let best = 0;
  for (const yOff of yOffsets) {
    let placements = 0;
    for (let y = b.minY + halfH + yOff; y <= b.maxY - halfH; y += rowStep) {
      let bestRow = 0;
      for (const xOff of xOffsets) {
        let row = 0;
        for (let x = b.minX + halfW + xOff; x <= b.maxX - halfW; x += colStep) {
          const rect = [
            [x - halfW, y - halfH],
            [x + halfW, y - halfH],
            [x + halfW, y + halfH],
            [x - halfW, y + halfH],
          ];
          const ctr = [x, y];
          if (pointInPoly(ctr, poly) && rect.every((pt) => pointInPoly(pt, poly))) {
            row++;
          }
        }
        if (row > bestRow) bestRow = row;
      }
      placements += bestRow;
    }
    if (placements > best) best = placements;
  }
  return best;
}
